// Today: the daily practice check-list is the centrepiece, with a Try
// Something New card above it when there are suggestions to consider.
// Nothing else in the main column; no outbound links on this surface.
//
// The suggestions are things to consider adding, never the day's to-do
// list (28 Jul 2026). The decision (add, later, no thanks) is made in the
// practice modal; a decided slot keeps its place for the day with the
// state shown. Doing lives in the check-list, which is frequency-aware: a
// practice that has met its cadence for the week rests under the More
// link, as do the unscheduled kinds (once, as needed), until ticked.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { burdenMinutes, readBurden } from "../../shared/burden.js";
import type { Descriptor } from "../shell/descriptor.js";
import { Avatar } from "../shell/Avatar.js";
import { burstConfetti } from "../shell/confetti.js";
import { doseNote, RailCard, rowTime, useExpert, weekDates, YOURS_AVATAR } from "../shell/fragments.js";
import { DiscoverHero } from "../shell/DiscoverHero.js";
import { dueToday } from "../state/todaycount.js";
import { useStore, type AppStore } from "../state/store.js";
import type { PlanItemView } from "../state/types.js";
import { artImg } from "../assets.js";

function prettyToday(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });
}

// What the week's bars actually say, in words (31 Jul 2026, Rob, replacing
// "A record, not a streak. Backfill a day you forgot; nothing resets." on a
// card with nothing to backfill with). It reads the days that have happened,
// never the ones still to come, and it never counts up a streak or scores
// the week: the rule is that passing costs nothing and nothing resets, so a
// quiet week is told plainly and a full one is named for what it is.
const COUNT_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven"];

export function weekNote(daysWith: number, daysSoFar: number): string {
  if (daysSoFar <= 1) {
    return daysWith === 0
      ? "The week starts here. One tick is the whole of the start."
      : "First day of the week, and something is done.";
  }
  if (daysWith === 0) {
    return daysSoFar <= 3
      ? "Nothing ticked yet. The week is young, and one is a start."
      : "A quiet week so far. Nothing was lost, and today still counts.";
  }
  if (daysWith === daysSoFar) {
    return `Something every day this week. ${daysSoFar >= 4 ? "That is a practice." : "That is a good run."}`;
  }
  if (daysWith >= 5) return `${COUNT_WORDS[daysWith]} days this week. That is a practice.`;
  if (daysWith >= 3) return `${COUNT_WORDS[daysWith]} days this week. That is traction.`;
  return daysWith === 1
    ? "One day so far this week, and it counts."
    : "Two days so far this week, and they count.";
}

// A ticked row's departure: hold with the check showing, then travel down
// into the More row. Must match the `tickaway` keyframes in app.css.
const LEAVE_MS = 720;
const LAND_FLASH_MS = 600;

// A check-list row: the whole row is the checkbox now that the tick reads
// as art (29 Jul 2026); only the chevron opens the practice modal. The
// card's image, or its topic's stand-in, sits behind the tick under the
// pane-tone veil, so every row carries the same square of glass.
function DailyRow({ item, leaving = false, onTick }: {
  item: PlanItemView;
  // Mid-departure: holding its place with the check showing before it
  // travels down into the More row (29 Jul 2026).
  leaving?: boolean;
  onTick?: (planItemId: string) => void;
}) {
  const s = useStore();
  const card = s.appState?.cards[item.cardId];
  const e = useExpert(card?.expertId ?? "");
  const ref = useRef<HTMLElement>(null);
  // The collapse closes exactly the gap the row leaves, so the rows below
  // rise to meet the More link instead of snapping when it unmounts.
  useLayoutEffect(() => {
    const el = ref.current;
    if (leaving && el) el.style.setProperty("--h", `${el.offsetHeight}px`);
  }, [leaving]);
  if (!card || !s.appState) return null;
  // Your own practice has no expert behind it: without this the avatar
  // lookup misses and renders the "??" fallback (reported live, 29 Jul).
  const yours = card.origin === "user";
  const done = item.week.includes(s.appState.today);
  const time = rowTime(card);
  const dose = doseNote(card, item.chosenVersion);
  const open = () => s.openSheet({ kind: "practice", cardId: card.id });
  const toggle = () => {
    if (!done) onTick?.(item.id);
    void s.toggleComplete(item.id, s.appState?.today ?? "", !done);
  };
  return (
    // The row opens the practice again and the box alone ticks it off (31 Jul
    // 2026, Rob, reversing 29 Jul): the whole row became the checkbox when the
    // tick carried the practice's art and read as the thing to press. With the
    // art gone the box is a box, and a row of words reads as something to
    // read.
    <article ref={ref} className="prow" data-done={done} data-leaving={leaving || undefined} onClick={open}>
      {/* A plain pane of glass (31 Jul 2026, Rob): the art came out of the
          checkboxes when the hero above took over carrying the pictures. */}
      <button
        className="tick"
        aria-pressed={done}
        aria-label={`${done ? "Done" : "Mark done"}: ${card.do}`}
        onClick={(ev) => {
          ev.stopPropagation();
          toggle();
        }}
      >
        ✓
      </button>
      <span style={{ display: "block", minWidth: 0 }}>
        <span className="do" style={{ display: "block" }}>
          {card.do}
        </span>
        {/* The time joins the who-and-when line after a dot (29 Jul 2026):
            it was a pill on the right, which read as a control and left the
            row's one text line saying less than it could. A chosen dose
            takes that slot when the user has moved off standard. */}
        <span className="from">
          <Avatar e={yours ? YOURS_AVATAR : e} size="xs" />
          {yours ? "Yours" : (e.short ?? e.name)} · {card.cadence ?? "Daily"}
          {dose || time ? ` · ${dose || time}` : ""}
        </span>
      </span>
      <button
        className="chev"
        aria-label="Open practice"
        onClick={(ev) => {
          ev.stopPropagation();
          open();
        }}
      >
        ›
      </button>
    </article>
  );
}

// The day's check-list: due rows, then the resting practices (done for
// the week, or unscheduled kinds like once and as needed) folded under a
// More link. Ticking a resting one counts it for today and lifts it into
// the day's list. Mark all done covers whatever is showing: the due list,
// plus the resting rows while they are open.
// The check-list's header: a button when there is a More section to open,
// plain markup when there is not, so nothing offers a fold that does nothing.
function HeadFrame({ toggle, open, children }: {
  toggle: (() => void) | null;
  open: boolean;
  children: React.ReactNode;
}) {
  if (!toggle) return <div className="bh">{children}</div>;
  return (
    <button className="bh bhtoggle" aria-expanded={open} onClick={toggle}>
      {children}
    </button>
  );
}

function DailySection({ due, rest, activeCount }: {
  due: PlanItemView[];
  rest: PlanItemView[];
  activeCount: number;
}) {
  const s = useStore();
  const nav = useStore((st) => st.nav);
  const markAll = useStore((st) => st.markAll);
  const [more, setMore] = useState(false);
  const today = s.appState?.today ?? "";
  const RING = 163.4;
  // A ticked row does not vanish (29 Jul 2026): it holds its place with the
  // check showing, then travels down into the More row, which flashes as it
  // lands. Without this the row disappeared under the cursor and the count
  // in a collapsed row changed silently.
  const [leaving, setLeaving] = useState<string[]>([]);
  const [landed, setLanded] = useState(false);
  const timers = useRef<number[]>([]);
  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);
  const startLeaving = (id: string) => {
    setLeaving((l) => (l.includes(id) ? l : [...l, id]));
    timers.current.push(
      window.setTimeout(() => {
        setLeaving((l) => l.filter((x) => x !== id));
        setLanded(true);
        timers.current.push(window.setTimeout(() => setLanded(false), LAND_FLASH_MS));
      }, LEAVE_MS),
    );
  };
  const inFlight = new Set(leaving);
  // Ticked rows fold under the one More link with the resting practices,
  // done-today first (29 Jul 2026, live; a separate Done collapse was
  // built the same day and cut within hours: two disclosure rows stacked
  // read as clutter). The list's job is what is left. A row still in flight
  // keeps its original position in the visible list until it lands.
  const doneRows = due.filter((p) => p.week.includes(today) && !inFlight.has(p.id));
  const todo = due.filter((p) => !p.week.includes(today) || inFlight.has(p.id));
  const folded = [...doneRows, ...rest];
  const foldedCount = folded.length + inFlight.size;
  // The ring fills on the tick, not on the landing: that is the feedback
  // the check itself is giving.
  const doneToday = due.filter((p) => p.week.includes(today)).length;
  const target = more ? [...due, ...rest] : due;
  const targetDone = target.filter((p) => p.week.includes(today)).length;

  // Confetti when the ring fills, bursting from the ring itself. Only on
  // the transition, so a page that loads already complete stays quiet.
  const orbitRef = useRef<HTMLDivElement>(null);
  const complete = due.length > 0 && doneToday === due.length;
  const wasComplete = useRef(complete);
  useEffect(() => {
    if (complete && !wasComplete.current) {
      const r = orbitRef.current?.getBoundingClientRect();
      burstConfetti(r ? r.left + r.width / 2 : undefined, r ? r.top + r.height / 2 : undefined);
    }
    wasComplete.current = complete;
  }, [complete]);

  return (
    <section className="brief glass daily">
      {/* The header is the second handle on the More section (31 Jul 2026,
          Rob): the same fold the link at the foot of the list opens, reachable
          from the top of the card. It is only a control when there is
          something folded away. */}
      <HeadFrame toggle={foldedCount > 0 ? () => setMore(!more) : null} open={more}>
        <div>
          <div className="overline">Your daily practice</div>
          {/* The heading carries the finished state itself (29 Jul 2026,
              live, third pass at this): a question over an empty list with
              a small answer underneath read as a hole twice. */}
          <h2>{complete ? "All done for today." : "What did you do today?"}</h2>
        </div>
        <span className="bhright">
          {due.length > 0 && (
            <div className="orbit" ref={orbitRef} aria-label={`${doneToday} of ${due.length} done today`}>
              <svg viewBox="0 0 64 64" aria-hidden="true">
                <circle className="track" cx="32" cy="32" r="26" />
                <circle
                  className="val"
                  cx="32"
                  cy="32"
                  r="26"
                  style={{ strokeDashoffset: RING * (1 - doneToday / due.length) }}
                />
              </svg>
              <span>
                {doneToday}
                <small>/{due.length}</small>
              </span>
            </div>
          )}
        </span>
      </HeadFrame>
      {todo.map((item) => (
        <DailyRow key={item.id} item={item} leaving={inFlight.has(item.id)} onTick={startLeaving} />
      ))}
      {due.length === 0 && (
        <p className="empty">Everything has met its cadence for the week. A quiet day is part of the practice.</p>
      )}
      {more && folded.map((it) => <DailyRow key={it.id} item={it} />)}
      {/* Counted with the rows still in flight, so the link is already
          on screen to be travelled into and its number does not jump
          when they land. */}
      {foldedCount > 0 && (
        <button
          className={`txt morelink${landed ? " landed" : ""}`}
          style={{ display: "block", marginTop: 8, fontSize: 12.5 }}
          onClick={() => setMore(!more)}
        >
          {more ? "Show fewer ▴" : `More · ${foldedCount} done or only as needed ▾`}
        </button>
      )}
      <div className="btnrow" style={{ marginTop: 14 }}>
        {target.length > 0 && (
          <button
            className="btn solid"
            onClick={() =>
              void markAll(
                target.map((p) => p.id),
                today,
                targetDone < target.length,
              )
            }
          >
            {targetDone < target.length ? "Mark all done" : "Unmark all"}
          </button>
        )}
        <button className="btn" onClick={() => nav("practices")}>
          Open my practice · {activeCount} ›
        </button>
      </div>
    </section>
  );
}

export function today(state: AppStore): Descriptor {
  const s = state.appState;
  const nav = state.nav;
  if (!s) {
    return {
      nav: "today",
      title: "Today",
      main: <section className="panel glass"><p className="empty">Loading your practice…</p></section>,
    };
  }

  const slots = s.todayNew;
  const fresh = slots.filter((slot) => slot.state === "new");
  // A burden can carry a range ("10 to 20 min"); the estimate takes the
  // average of low and high (28 Jul 2026). The parser owns the reading now
  // (29 Jul 2026), which is why "ongoing" and "one-time" no longer count as
  // the five minutes a missing number used to.
  const minutes = Math.max(
    Math.round(fresh.reduce((n, slot) => n + burdenMinutes(readBurden(s.cards[slot.cardId]?.burden).minutes), 0)),
    1,
  );
  const tryHeading = fresh.length
    ? `${["", "One new thing", "Two new things", "Three new things"][fresh.length] ?? "New things"}. About ${minutes} ${minutes === 1 ? "minute" : "minutes"}.`
    : "All decided for today.";

  // The day's list is frequency-aware: due until the cadence target for
  // the week is met, resting after. Anything ticked today stays visible
  // and counted, whatever its cadence. `dueToday` is shared with the nav
  // badge so the two never disagree.
  const activePlan = s.plan.filter((p) => p.status === "active");
  const due = dueToday(s);
  const rest = activePlan.filter((p) => !due.includes(p));

  // Rhythm bars: how many checks landed each day, against the week's best.
  const dates = weekDates(s.today);
  const checks = dates.map((date) => (date > s.today ? null : activePlan.filter((p) => p.week.includes(date)).length));
  const maxChecks = Math.max(1, ...checks.map((n) => n ?? 0));
  // Days that have happened, and how many of them carried something. Today
  // counts as a day in progress, so a morning with nothing ticked reads as a
  // day still open rather than a day missed.
  const daysSoFar = checks.filter((n) => n !== null).length;
  const daysWithChecks = checks.filter((n) => n !== null && n > 0).length;

  const main = (
    <>
      {slots.length > 0 && (
        <section className="brief glass">
          <div className="bh">
            <div>
              {/* The day's date leads the panel (31 Jul 2026, Rob), in the
                  slot the section label held: it moved down here when the
                  page eyebrows came off. */}
              <div className="overline">{prettyToday(s.today)}</div>
              <h2>{tryHeading}</h2>
              <p className="copy">
                {fresh.length
                  ? "Review these to see if you want to add any to your weekly practice."
                  : "Check back each day for more from the people you follow."}
              </p>
            </div>
          </div>
          {/* The day's new things as one rotating picture rather than a
              stack of rows (31 Jul 2026, Rob), the same hero Discover and
              the feed lead with. Nothing else about the section moves: the
              heading still counts what is left, a decided practice still
              leaves the deck, and the More row still sits underneath. */}
          {fresh.length > 0 && (
            <DiscoverHero
              inset
              cards={fresh.map((slot) => s.cards[slot.cardId]).filter((c) => c !== undefined)}
            />
          )}
          {/* No count on the row (31 Jul 2026, Rob): the nav badge already
              carries the New number, and a second tally on Today made the
              row read as a queue to work through. */}
          <button className="morerow" onClick={() => nav("discover")}>
            <span className="plus">+</span>
            <span>
              <strong>Discover more practices</strong>
            </span>
          </button>
        </section>
      )}

      {activePlan.length > 0 && <DailySection due={due} rest={rest} activeCount={activePlan.length} />}

      {slots.length === 0 && activePlan.length === 0 && (
        <section className="panel glass">
          <p className="empty">
            Nothing new today. Follow more people and their practices appear here.
          </p>
        </section>
      )}
    </>
  );

  const rail = (
    <>
      {s.feedPreview.length > 0 && (
        <RailCard eyebrow="From people you follow" title="Worth a look">
          {s.feedPreview.map((it) => (
            <FeedPreviewRow key={it.id} id={it.id} expertId={it.expertId} title={it.title} kind={it.kind} image={it.image} />
          ))}
          <button className="btn blockbtn" onClick={() => nav("feed")}>
            Open feed ›
          </button>
        </RailCard>
      )}
      <RailCard eyebrow="Your rhythm" title="This week">
        <div className="bars" aria-label="Checks by day">
          {checks.map((n, i) => (
            <div key={i} className={dates[i] === s.today ? "is-today" : ""}>
              <span
                style={{
                  ["--bar" as string]: `${n === null ? 0 : Math.max((n / maxChecks) * 100, dates[i] === s.today ? 8 : 0)}%`,
                }}
              />
              <small>{["M", "T", "W", "T", "F", "S", "S"][i]}</small>
            </div>
          ))}
        </div>
        <p style={{ marginTop: 10 }}>{weekNote(daysWithChecks, daysSoFar)}</p>
        <button className="btn blockbtn" onClick={() => nav("practices")}>
          Open my practice ›
        </button>
      </RailCard>
      <RailCard eyebrow="Sunday" title="Weekly review">
        <p>Keep, adjust, or shelve each practice. Ten minutes, no guilt.</p>
        <div className="btnrow" style={{ marginTop: 12 }}>
          <button className="btn solid" style={{ flex: 1, textAlign: "center" }} onClick={() => nav("review")}>
            Start ›
          </button>
        </div>
      </RailCard>
    </>
  );

  return { nav: "today", title: "Today", main, rail };
}

// Feed preview rows navigate to the feed, never out of the app: no
// outbound links on Today.
function FeedPreviewRow({ id, expertId, title, kind, image }: {
  id: string;
  expertId: string;
  title: string;
  kind: string | null;
  image: string | null;
}) {
  const nav = useStore((s) => s.nav);
  const e = useExpert(expertId);
  return (
    <button className="lrow" onClick={() => nav("feed", id)}>
      {image ? (
        <img className="uimg" alt="" loading="lazy" sizes="64px" {...artImg(image)} />
      ) : (
        <Avatar e={e} size="m" />
      )}
      <span className="grow">
        <strong>{title}</strong>
        <small>
          {e.name}
          {kind ? ` · ${kind}` : ""}
        </small>
      </span>
      <span className="chev">›</span>
    </button>
  );
}
